import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  TeContainer,
  TeContainerSlim,
  TeText,
  TeButton,
  TeRow,
  TeCell,
  TeTable,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { PlaceholderContainer, useNotification } from '@topcon/topcon-utils';
import styled from 'styled-components';

import { Section } from '../../utils/styling';
import { AppRoutes } from '../../app/routes/utils';
import { useRequestChangeOwnership } from './changeOwnership.queries';
import { useAcceptChangeOwnershipRequest } from './changeOwnership.mutations';
import CompanyIcon from '../../components/Company/CompanyIcon';
import IconWrapper from '../../components/IconWrapper';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';

function ReviewRequestChangeOwnership() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const id = searchParams.get('resource');

  const { data: changeOwnershipRequest, isLoading, isError } = useRequestChangeOwnership(id);

  const { mutateAsync: acceptRequest, isPending: isAccepting } = useAcceptChangeOwnershipRequest();

  if (!id || isError) {
    navigate(AppRoutes.Overview);
    return null;
  }

  const handleAccept = async () => {
    if (!id) return;

    try {
      await acceptRequest({ id });
      navigate(AppRoutes.Overview);
    } catch (error: unknown) {
      if (!showError(error)) {
        notificationService.addNotification(
          t('ownershipInvitation.acceptError'),
          t('ownershipInvitation.acceptErrorDescription'),
          'error'
        );
      }
    }
  };

  const businessPartner = changeOwnershipRequest?.businessPartner;
  const address = businessPartner?.companyAddress;

  return (
    <TeContainer column grow padding="s" gap="xs" overflow="auto" alignItems="center">
      <Section $width="s">
        <TeContainer column fill grow padding={['m', 'l']} gap="m" justifyContent="space-between">
          <TeContainerSlim column gap="xs2" fill>
            <TeText size={20} weight={700}>
              {t('ownershipInvitation.title')}
            </TeText>
            <TeText color="text.secondary" size={14}>
              {t('ownershipInvitation.description')}
            </TeText>
          </TeContainerSlim>

          {isLoading ? (
            <PlaceholderContainer hasLoader text={t('ownershipInvitation.loading')} />
          ) : businessPartner ? (
            <CompanyCard
              backgroundColor="background.tertiary"
              padding={['xl', 'l']}
              fill
              grow
              column
              borderRadius="m"
              gap="m"
            >
              <TeContainerSlim gap="m" column fill>
                <GreenIconWrapper size={64} backgroundColor="#4caf50">
                  <CompanyIcon size="xl" iconURL={undefined} />
                </GreenIconWrapper>
                <TeContainerSlim column gap="xs2" fill>
                  <TeText size={20} weight={700}>
                    {businessPartner.name}
                  </TeText>
                  <TeText size={12} weight={400} color="text.secondary">
                    {t('businessCompany')}
                  </TeText>
                </TeContainerSlim>
              </TeContainerSlim>
              <TeContainerSlim gap="xs" fill grow column>
                <TeTable evenOdd>
                  <TeRow>
                    <TeCell>
                      <TeText color="text.secondary" size={12} weight={400}>
                        {t('officeAddress')}
                      </TeText>
                    </TeCell>
                  </TeRow>
                  {address?.addressLineOne && (
                    <TeRow>
                      <TeCell>
                        <TeText size={16} weight={500}>
                          {address.addressLineOne}
                        </TeText>
                      </TeCell>
                    </TeRow>
                  )}
                  {(address?.zipPostalCode || address?.city) && (
                    <TeRow>
                      <TeCell>
                        <TeText size={16} weight={500}>
                          {[address.zipPostalCode, address.city].filter(Boolean).join(' ')}
                        </TeText>
                      </TeCell>
                    </TeRow>
                  )}
                  {address?.country && (
                    <TeRow>
                      <TeCell>
                        <TeText size={16} weight={500}>
                          {address.country}
                        </TeText>
                      </TeCell>
                    </TeRow>
                  )}
                </TeTable>
              </TeContainerSlim>
            </CompanyCard>
          ) : null}

          <TeContainerSlim justifyContent="flex-end" fill>
            <TeButton
              text={t('ownershipInvitation.acceptButton')}
              teclick={handleAccept}
              disabled={isAccepting || !businessPartner}
              loading={isAccepting}
            />
          </TeContainerSlim>
        </TeContainer>
      </Section>
    </TeContainer>
  );
}

const CompanyCard = styled(TeContainer)`
  width: 100% !important;
`;

const GreenIconWrapper = styled(IconWrapper)`
  background-color: #4caf50 !important;
  outline: none !important;
`;

export default ReviewRequestChangeOwnership;
